import { Link, useLocation } from "@tanstack/react-router";
import { Bot, Home, Settings, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { dashboardNavItems } from "../header/shared";

const iconMap = {
	home: Home,
	bot: Bot,
	settings: Settings,
	user: User,
} as const;

interface DashboardBottomNavProps {
	className?: string;
}

function DashboardBottomNav({ className }: DashboardBottomNavProps) {
	const location = useLocation();
	const normalizedPath = location.pathname.replace(/\/+$/, "");
	const lastSegment = normalizedPath.split("/").filter(Boolean).pop();
	const activeSegment =
		normalizedPath === "/dashboard"
			? ""
			: dashboardNavItems.some((item) => item.segment === lastSegment)
				? lastSegment
				: null;

	return (
		<nav
			className={cn(
				"fixed bottom-0 inset-x-0 h-16 bg-background/95 backdrop-blur-xl border-t border-border dark:border-accent lg:hidden z-50",
				"pb-[env(safe-area-inset-bottom,0px)]",
				className,
			)}
		>
			<div className="flex items-center justify-around h-full max-w-2xl mx-auto">
				{dashboardNavItems.map((item) => {
					const Icon = iconMap[item.icon];
					const isActive = activeSegment === item.segment;

					return (
						<Link
							key={item.segment}
							to={item.path}
							aria-current={isActive ? "page" : undefined}
							className={cn(
								"flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 relative transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset",
								isActive
									? "text-brand"
									: "text-muted-foreground hover:text-brand",
							)}
						>
							<Icon className="size-4" />
							<span className="font-mono text-sm uppercase tracking-[0.12em]">
								{item.label}
							</span>
							{isActive && (
								<div className="absolute top-0 inset-x-0 h-0.5 bg-brand" />
							)}
						</Link>
					);
				})}
			</div>
		</nav>
	);
}

export { DashboardBottomNav };
